import React, { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Users, Building2, TrendingUp, Wallet, FileText, 
  ChevronRight, ChevronLeft, Check, Rocket, Upload,
  Linkedin, Globe, Mail, Phone
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const STEPS = [
  { id: 0, title: 'Founders & Team', icon: Users },
  { id: 1, title: 'Company & Structure', icon: Building2 },
  { id: 2, title: 'Business & Traction', icon: TrendingUp },
  { id: 3, title: 'Funding Ask', icon: Wallet },
  { id: 4, title: 'Documents & Data Room', icon: FileText },
];

const SECTORS = [
  'AI & Machine Learning', 'SaaS / B2B Tech', 'Fintech & Payments',
  'Healthcare & MedTech', 'Biotech & Life Sciences', 'EdTech',
  'Climate & Clean Energy', 'D2C & Consumer', 'Logistics & Supply Chain',
  'Deep Tech', 'Agritech & FoodTech', 'Mobility & EVs',
  'Web3 & Crypto', 'Media & Creator Economy', 'Other'
];

const StartupOnboarding = ({ user, onComplete }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState('');
  
  // Form data state
  const [formData, setFormData] = useState({
    // Step 0: Founders & Team
    founderName: '',
    founderDesignation: '',
    founderEmail: user?.email || '',
    founderMobile: '',
    founderLinkedIn: '',
    founderExperience: '',
    founderEducation: '',
    founderBio: '',
    coFounder2Name: '',
    coFounder2Role: '',
    coFounder3Name: '',
    coFounder3Role: '',
    teamSize: '',
    advisors: '',
    
    // Step 1: Company & Structure
    companyName: '',
    brandName: '',
    website: '',
    entityType: '',
    countryOfIncorporation: '',
    cin: '',
    yearFounded: '',
    registeredAddress: '',
    operatingCity: '',
    operatingCountries: '',
    foundersOwnership: '',
    esopPool: '',
    existingInvestors: '',
    currency: 'INR',
    
    // Step 2: Business & Traction
    primarySector: '',
    oneLineDescription: '',
    problemSolution: '',
    businessModel: '',
    targetCustomer: '',
    currentStage: '',
    monthlyRevenue: '',
    revenueGrowth: '',
    grossMargin: '',
    totalCustomers: '',
    cac: '',
    ltv: '',
    monthlyBurn: '',
    runway: '',
    keyMilestones: '',
    competitiveLandscape: '',
    
    // Step 3: Funding Ask
    currentRound: '',
    totalRoundSize: '',
    amountRaised: '',
    amountSeeking: '',
    preMoneyValuation: '',
    instrument: '',
    minTicketSize: '',
    targetCloseDate: '',
    useOfFundsProduct: '',
    useOfFundsSales: '',
    useOfFundsTeam: '',
    useOfFundsOperations: '',
    useOfFundsOther: '',
    useOfFundsNarrative: '',
    openToStrategic: '',
    previousFunding: '',
    previousInvestors: '',
    
    // Step 4: Documents
    pitchDeckUrl: '',
    financialModelUrl: '',
    dataRoomUrl: '',
    productDemoUrl: '',
    ipStatus: '',
    regulatoryApprovals: '',
    legalIssues: '',
    additionalInfo: '',
    termsAgreed: false,
    privacyAgreed: false,
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 0:
        return formData.founderName && formData.founderDesignation && 
               formData.founderEmail && formData.founderMobile && formData.teamSize;
      case 1:
        return formData.companyName && formData.entityType && 
               formData.countryOfIncorporation && formData.yearFounded;
      case 2:
        return formData.primarySector && formData.oneLineDescription && 
               formData.problemSolution && formData.businessModel && formData.currentStage;
      case 3:
        return formData.currentRound && formData.totalRoundSize && 
               formData.preMoneyValuation && formData.instrument;
      case 4:
        return formData.termsAgreed && formData.privacyAgreed;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (!validateStep(currentStep)) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      toast.error('Please agree to the terms and privacy policy');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/startups/onboarding`, formData, {
        withCredentials: true
      });
      
      setReferenceNumber(response.data.reference_number || `SBB-${Date.now().toString(36).toUpperCase()}`);
      setCompleted(true);
      if (onComplete) onComplete();
      toast.success('Application submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const goToStep = (stepId) => {
    // Only allow going to completed steps or current step
    if (stepId <= currentStep) {
      setCurrentStep(stepId);
    }
  };

  if (completed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-6">
        <Card className="bg-slate-800/40 border-slate-700 p-12 max-w-lg text-center">
          <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-slate-100 mb-4">Application Submitted!</h2>
          <p className="text-slate-400 mb-6">
            Thank you for submitting your startup profile. Our team will review your application and get back to you within 5-7 business days.
          </p>
          <div className="bg-slate-700/30 rounded-lg p-4 mb-6">
            <p className="text-sm text-slate-400">Reference Number</p>
            <p className="text-xl font-mono font-bold text-cyan-400">{referenceNumber}</p>
          </div>
          <Button
            onClick={() => navigate('/dashboard')}
            className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
          >
            Go to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" data-testid="startup-onboarding">
      {/* Header */}
      <nav className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <div className="w-9 h-9 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
              <Rocket className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              StartupBlackbox
            </span>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-slate-400 hover:text-slate-200"
          >
            Back to Dashboard
          </Button>
        </div>
      </nav>

      <div className="flex">
        {/* Sidebar */}
        <aside className="hidden lg:block w-80 min-h-screen border-r border-slate-800 bg-slate-900/30 p-6">
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-slate-100 mb-2">List Your Startup</h2>
            <p className="text-sm text-slate-400">Complete all steps to submit your application</p>
          </div>
          
          <nav className="space-y-2">
            {STEPS.map((step, idx) => {
              const Icon = step.icon;
              const isActive = idx === currentStep;
              const isCompleted = idx < currentStep;
              
              return (
                <button
                  key={step.id}
                  onClick={() => goToStep(idx)}
                  className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all text-left ${
                    isActive 
                      ? 'bg-cyan-500/20 border border-cyan-500/30 text-cyan-400' 
                      : isCompleted
                        ? 'bg-green-500/10 border border-green-500/20 text-green-400 hover:bg-green-500/20'
                        : 'text-slate-500 hover:bg-slate-800/50'
                  }`}
                  data-testid={`step-nav-${idx}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    isActive 
                      ? 'bg-cyan-500/20' 
                      : isCompleted 
                        ? 'bg-green-500/20' 
                        : 'bg-slate-800'
                  }`}>
                    {isCompleted ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Icon className="w-4 h-4" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{step.title}</p>
                    <p className="text-xs opacity-60">Step {idx + 1} of {STEPS.length}</p>
                  </div>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-12">
          <div className="max-w-3xl mx-auto">
            {/* Mobile Step Indicator */}
            <div className="lg:hidden mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-400">Step {currentStep + 1} of {STEPS.length}</span>
                <span className="text-sm font-medium text-slate-200">{STEPS[currentStep].title}</span>
              </div>
              <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-cyan-500 to-blue-600 transition-all duration-300"
                  style={{ width: `${((currentStep + 1) / STEPS.length) * 100}%` }}
                />
              </div>
            </div>

            {/* Step Header */}
            <div className="mb-8">
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-100 mb-2">{STEPS[currentStep].title}</h1>
              <p className="text-slate-400">
                {currentStep === 0 && 'Tell us about the founding team and key members'}
                {currentStep === 1 && 'Company details and legal structure'}
                {currentStep === 2 && 'Your business model, traction, and market'}
                {currentStep === 3 && 'Current fundraising details and use of funds'}
                {currentStep === 4 && 'Upload key documents and finalize your application'}
              </p>
            </div>

            {/* Form Steps */}
            <div className="space-y-6">
              {/* Step 0: Founders & Team */}
              {currentStep === 0 && (
                <div className="space-y-6">
                  <Card className="bg-slate-800/40 border-slate-700 p-6">
                    <h3 className="text-lg font-semibold text-slate-100 mb-4">Primary Founder</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Full Name *</Label>
                        <Input
                          value={formData.founderName}
                          onChange={(e) => updateField('founderName', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="John Doe"
                          data-testid="founder-name"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Designation / Title *</Label>
                        <Input
                          value={formData.founderDesignation}
                          onChange={(e) => updateField('founderDesignation', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="CEO & Co-founder"
                          data-testid="founder-designation"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Email Address *</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                          <Input
                            type="email"
                            value={formData.founderEmail}
                            onChange={(e) => updateField('founderEmail', e.target.value)}
                            className="mt-1 bg-slate-800 border-slate-700 text-slate-100 pl-10"
                            placeholder="john@company.com"
                            data-testid="founder-email"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-slate-300">Mobile / WhatsApp *</Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                          <Input
                            type="tel"
                            value={formData.founderMobile}
                            onChange={(e) => updateField('founderMobile', e.target.value)}
                            className="mt-1 bg-slate-800 border-slate-700 text-slate-100 pl-10"
                            placeholder="+91 98765 43210"
                            data-testid="founder-mobile"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-slate-300">LinkedIn Profile</Label>
                        <div className="relative">
                          <Linkedin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                          <Input
                            type="url"
                            value={formData.founderLinkedIn}
                            onChange={(e) => updateField('founderLinkedIn', e.target.value)}
                            className="mt-1 bg-slate-800 border-slate-700 text-slate-100 pl-10"
                            placeholder="linkedin.com/in/johndoe"
                            data-testid="founder-linkedin"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-slate-300">Years of Experience</Label>
                        <Select value={formData.founderExperience} onValueChange={(v) => updateField('founderExperience', v)}>
                          <SelectTrigger className="mt-1 bg-slate-800 border-slate-700 text-slate-100">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0-2">0 – 2 years (first-time founder)</SelectItem>
                            <SelectItem value="3-5">3 – 5 years</SelectItem>
                            <SelectItem value="5-10">5 – 10 years</SelectItem>
                            <SelectItem value="10+">10+ years</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-slate-300">Education Background</Label>
                        <Input
                          value={formData.founderEducation}
                          onChange={(e) => updateField('founderEducation', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="MBA from IIM, B.Tech from IIT"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-slate-300">Brief Founder Bio</Label>
                        <Textarea
                          value={formData.founderBio}
                          onChange={(e) => updateField('founderBio', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100 min-h-[100px]"
                          placeholder="Tell us about your background, previous ventures, and why you're building this..."
                        />
                      </div>
                    </div>
                  </Card>

                  <Card className="bg-slate-800/40 border-slate-700 p-6">
                    <h3 className="text-lg font-semibold text-slate-100 mb-4">Co-Founders (Optional)</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Co-Founder 2 Name</Label>
                        <Input
                          value={formData.coFounder2Name}
                          onChange={(e) => updateField('coFounder2Name', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="Jane Smith"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Co-Founder 2 Role</Label>
                        <Input
                          value={formData.coFounder2Role}
                          onChange={(e) => updateField('coFounder2Role', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="CTO & Co-founder"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Co-Founder 3 Name</Label>
                        <Input
                          value={formData.coFounder3Name}
                          onChange={(e) => updateField('coFounder3Name', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Co-Founder 3 Role</Label>
                        <Input
                          value={formData.coFounder3Role}
                          onChange={(e) => updateField('coFounder3Role', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                        />
                      </div>
                    </div>
                  </Card>

                  <Card className="bg-slate-800/40 border-slate-700 p-6">
                    <h3 className="text-lg font-semibold text-slate-100 mb-4">Team</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Total Team Size *</Label>
                        <Select value={formData.teamSize} onValueChange={(v) => updateField('teamSize', v)}>
                          <SelectTrigger className="mt-1 bg-slate-800 border-slate-700 text-slate-100" data-testid="team-size">
                            <SelectValue placeholder="Select team size" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1-5">1 – 5 (Founders only / very early)</SelectItem>
                            <SelectItem value="6-15">6 – 15</SelectItem>
                            <SelectItem value="16-50">16 – 50</SelectItem>
                            <SelectItem value="51-200">51 – 200</SelectItem>
                            <SelectItem value="200+">200+</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-300">Key Advisors / Board Members</Label>
                        <Input
                          value={formData.advisors}
                          onChange={(e) => updateField('advisors', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="Comma-separated names"
                        />
                      </div>
                    </div>
                  </Card>
                </div>
              )}

              {/* Step 1: Company & Structure */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  <Card className="bg-slate-800/40 border-slate-700 p-6">
                    <h3 className="text-lg font-semibold text-slate-100 mb-4">Company Details</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Company / Startup Name *</Label>
                        <Input
                          value={formData.companyName}
                          onChange={(e) => updateField('companyName', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="Acme Technologies Pvt Ltd"
                          data-testid="company-name"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Brand / Trading Name (if different)</Label>
                        <Input
                          value={formData.brandName}
                          onChange={(e) => updateField('brandName', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="Acme"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Website</Label>
                        <div className="relative">
                          <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                          <Input
                            type="url"
                            value={formData.website}
                            onChange={(e) => updateField('website', e.target.value)}
                            className="mt-1 bg-slate-800 border-slate-700 text-slate-100 pl-10"
                            placeholder="https://acme.com"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-slate-300">Preferred Currency</Label>
                        <Select value={formData.currency} onValueChange={(v) => updateField('currency', v)}>
                          <SelectTrigger className="mt-1 bg-slate-800 border-slate-700 text-slate-100">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="INR">₹ INR (Indian Rupee)</SelectItem>
                            <SelectItem value="USD">$ USD (US Dollar)</SelectItem>
                            <SelectItem value="EUR">€ EUR (Euro)</SelectItem>
                            <SelectItem value="GBP">£ GBP (British Pound)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </Card>

                  <Card className="bg-slate-800/40 border-slate-700 p-6">
                    <h3 className="text-lg font-semibold text-slate-100 mb-4">Legal Structure</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Entity Type *</Label>
                        <Select value={formData.entityType} onValueChange={(v) => updateField('entityType', v)}>
                          <SelectTrigger className="mt-1 bg-slate-800 border-slate-700 text-slate-100" data-testid="entity-type">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pvt-ltd-india">Private Limited Company (India)</SelectItem>
                            <SelectItem value="llp">LLP</SelectItem>
                            <SelectItem value="opc">OPC</SelectItem>
                            <SelectItem value="inc-usa">Incorporated (USA / Cayman / Singapore)</SelectItem>
                            <SelectItem value="partnership">Partnership</SelectItem>
                            <SelectItem value="proprietorship">Proprietorship</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-300">Country of Incorporation *</Label>
                        <Select value={formData.countryOfIncorporation} onValueChange={(v) => updateField('countryOfIncorporation', v)}>
                          <SelectTrigger className="mt-1 bg-slate-800 border-slate-700 text-slate-100" data-testid="country">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="india">India</SelectItem>
                            <SelectItem value="usa">USA (Delaware)</SelectItem>
                            <SelectItem value="singapore">Singapore</SelectItem>
                            <SelectItem value="cayman">Cayman Islands</SelectItem>
                            <SelectItem value="uk">UK</SelectItem>
                            <SelectItem value="uae">UAE</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-300">CIN / Registration Number</Label>
                        <Input
                          value={formData.cin}
                          onChange={(e) => updateField('cin', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="U12345MH2020PTC123456"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Year Founded *</Label>
                        <Input
                          type="number"
                          min="1990"
                          max="2025"
                          value={formData.yearFounded}
                          onChange={(e) => updateField('yearFounded', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="2022"
                          data-testid="year-founded"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-slate-300">Registered Address</Label>
                        <Input
                          value={formData.registeredAddress}
                          onChange={(e) => updateField('registeredAddress', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="123 Business Park, Mumbai, Maharashtra 400001"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Primary Operating City</Label>
                        <Input
                          value={formData.operatingCity}
                          onChange={(e) => updateField('operatingCity', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="Bangalore"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Operating Countries / Markets</Label>
                        <Input
                          value={formData.operatingCountries}
                          onChange={(e) => updateField('operatingCountries', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="India, USA, Southeast Asia"
                        />
                      </div>
                    </div>
                  </Card>

                  <Card className="bg-slate-800/40 border-slate-700 p-6">
                    <h3 className="text-lg font-semibold text-slate-100 mb-4">Ownership & Cap Table</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Founders' Combined Ownership</Label>
                        <Select value={formData.foundersOwnership} onValueChange={(v) => updateField('foundersOwnership', v)}>
                          <SelectTrigger className="mt-1 bg-slate-800 border-slate-700 text-slate-100">
                            <SelectValue placeholder="Select range" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="80-100">80 – 100% (pre-institutional)</SelectItem>
                            <SelectItem value="60-80">60 – 80%</SelectItem>
                            <SelectItem value="40-60">40 – 60%</SelectItem>
                            <SelectItem value="below-40">Below 40%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-300">ESOP Pool (if any)</Label>
                        <Select value={formData.esopPool} onValueChange={(v) => updateField('esopPool', v)}>
                          <SelectTrigger className="mt-1 bg-slate-800 border-slate-700 text-slate-100">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None yet</SelectItem>
                            <SelectItem value="less-5">Less than 5%</SelectItem>
                            <SelectItem value="5-10">5 – 10%</SelectItem>
                            <SelectItem value="10-15">10 – 15%</SelectItem>
                            <SelectItem value="above-15">Above 15%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-slate-300">Existing Investors (if any)</Label>
                        <Textarea
                          value={formData.existingInvestors}
                          onChange={(e) => updateField('existingInvestors', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="List existing investors with approximate amounts..."
                        />
                      </div>
                    </div>
                  </Card>
                </div>
              )}

              {/* Step 2: Business & Traction */}
              {currentStep === 2 && (
                <div className="space-y-6">
                  <Card className="bg-slate-800/40 border-slate-700 p-6">
                    <h3 className="text-lg font-semibold text-slate-100 mb-4">Sector & Description</h3>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-slate-300 mb-3 block">Primary Sector *</Label>
                        <div className="flex flex-wrap gap-2">
                          {SECTORS.map((sector) => (
                            <button
                              key={sector}
                              type="button"
                              onClick={() => updateField('primarySector', sector)}
                              className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                                formData.primarySector === sector
                                  ? 'bg-cyan-500 text-white'
                                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                              }`}
                              data-testid={`sector-${sector.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {sector}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label className="text-slate-300">One-line Description *</Label>
                        <Input
                          value={formData.oneLineDescription}
                          onChange={(e) => updateField('oneLineDescription', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="We help SMEs automate their invoicing and payments"
                          data-testid="one-line-desc"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Problem & Solution *</Label>
                        <Textarea
                          value={formData.problemSolution}
                          onChange={(e) => updateField('problemSolution', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100 min-h-[120px]"
                          placeholder="Describe the problem you're solving and how your solution addresses it..."
                          data-testid="problem-solution"
                        />
                      </div>
                    </div>
                  </Card>

                  <Card className="bg-slate-800/40 border-slate-700 p-6">
                    <h3 className="text-lg font-semibold text-slate-100 mb-4">Business Model</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Business Model *</Label>
                        <Select value={formData.businessModel} onValueChange={(v) => updateField('businessModel', v)}>
                          <SelectTrigger className="mt-1 bg-slate-800 border-slate-700 text-slate-100" data-testid="business-model">
                            <SelectValue placeholder="Select primary model" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="saas">SaaS — subscription</SelectItem>
                            <SelectItem value="marketplace">Marketplace</SelectItem>
                            <SelectItem value="transactional">Transactional / Commission</SelectItem>
                            <SelectItem value="d2c">D2C / E-commerce</SelectItem>
                            <SelectItem value="licensing">Licensing</SelectItem>
                            <SelectItem value="freemium">Freemium</SelectItem>
                            <SelectItem value="hybrid">Hybrid</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-300">Target Customer</Label>
                        <Select value={formData.targetCustomer} onValueChange={(v) => updateField('targetCustomer', v)}>
                          <SelectTrigger className="mt-1 bg-slate-800 border-slate-700 text-slate-100">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="b2b-enterprise">B2B — enterprises</SelectItem>
                            <SelectItem value="b2b-sme">B2B — SMEs</SelectItem>
                            <SelectItem value="b2c">B2C</SelectItem>
                            <SelectItem value="b2b2c">B2B2C</SelectItem>
                            <SelectItem value="government">Government / PSU</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="mt-4">
                      <Label className="text-slate-300 mb-3 block">Current Stage *</Label>
                      <div className="grid md:grid-cols-2 gap-3">
                        {[
                          { value: 'idea', label: 'Idea / MVP', desc: 'Concept validated, building' },
                          { value: 'mvp', label: 'MVP / Beta', desc: 'Product live, early users' },
                          { value: 'revenue', label: 'Revenue Stage', desc: 'Paying customers, early traction' },
                          { value: 'growth', label: 'Growth Stage', desc: 'Scaling revenue & team' },
                        ].map((stage) => (
                          <button
                            key={stage.value}
                            type="button"
                            onClick={() => updateField('currentStage', stage.value)}
                            className={`p-4 rounded-lg border text-left transition-all ${
                              formData.currentStage === stage.value
                                ? 'border-cyan-500 bg-cyan-500/10'
                                : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                            }`}
                            data-testid={`stage-${stage.value}`}
                          >
                            <p className={`font-medium ${formData.currentStage === stage.value ? 'text-cyan-400' : 'text-slate-200'}`}>
                              {stage.label}
                            </p>
                            <p className="text-sm text-slate-400">{stage.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  </Card>

                  <Card className="bg-slate-800/40 border-slate-700 p-6">
                    <h3 className="text-lg font-semibold text-slate-100 mb-4">Key Metrics</h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-slate-300">Monthly Revenue (MRR/ARR)</Label>
                        <Input
                          value={formData.monthlyRevenue}
                          onChange={(e) => updateField('monthlyRevenue', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="₹5L / $10K"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Revenue Growth (MoM %)</Label>
                        <Input
                          value={formData.revenueGrowth}
                          onChange={(e) => updateField('revenueGrowth', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="15%"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Gross Margin %</Label>
                        <Input
                          value={formData.grossMargin}
                          onChange={(e) => updateField('grossMargin', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="70%"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Total Customers / Users</Label>
                        <Input
                          value={formData.totalCustomers}
                          onChange={(e) => updateField('totalCustomers', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="500"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">CAC (Customer Acquisition Cost)</Label>
                        <Input
                          value={formData.cac}
                          onChange={(e) => updateField('cac', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="₹2,000"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">LTV (Customer Lifetime Value)</Label>
                        <Input
                          value={formData.ltv}
                          onChange={(e) => updateField('ltv', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="₹50,000"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Monthly Burn Rate</Label>
                        <Input
                          value={formData.monthlyBurn}
                          onChange={(e) => updateField('monthlyBurn', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="₹10L"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Runway (months remaining)</Label>
                        <Input
                          value={formData.runway}
                          onChange={(e) => updateField('runway', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="12"
                        />
                      </div>
                    </div>
                  </Card>

                  <Card className="bg-slate-800/40 border-slate-700 p-6">
                    <h3 className="text-lg font-semibold text-slate-100 mb-4">Milestones & Competition</h3>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-slate-300">Key Milestones & Achievements *</Label>
                        <Textarea
                          value={formData.keyMilestones}
                          onChange={(e) => updateField('keyMilestones', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100 min-h-[100px]"
                          placeholder="List your key achievements, product launches, partnerships, awards..."
                          data-testid="key-milestones"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Competitive Landscape</Label>
                        <Textarea
                          value={formData.competitiveLandscape}
                          onChange={(e) => updateField('competitiveLandscape', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100 min-h-[100px]"
                          placeholder="Who are your main competitors? What's your unfair advantage?"
                        />
                      </div>
                    </div>
                  </Card>
                </div>
              )}

              {/* Step 3: Funding Ask */}
              {currentStep === 3 && (
                <div className="space-y-6">
                  <Card className="bg-slate-800/40 border-slate-700 p-6">
                    <h3 className="text-lg font-semibold text-slate-100 mb-4">Current Round Details</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Current Fundraising Round *</Label>
                        <Select value={formData.currentRound} onValueChange={(v) => updateField('currentRound', v)}>
                          <SelectTrigger className="mt-1 bg-slate-800 border-slate-700 text-slate-100" data-testid="current-round">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pre-seed">Pre-Seed</SelectItem>
                            <SelectItem value="seed">Seed</SelectItem>
                            <SelectItem value="seed-ext">Seed Extension</SelectItem>
                            <SelectItem value="series-a">Series A</SelectItem>
                            <SelectItem value="series-b">Series B</SelectItem>
                            <SelectItem value="series-c+">Series C+</SelectItem>
                            <SelectItem value="bridge">Bridge Round</SelectItem>
                            <SelectItem value="pre-ipo">Pre-IPO</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-300">Total Round Size *</Label>
                        <Input
                          value={formData.totalRoundSize}
                          onChange={(e) => updateField('totalRoundSize', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="₹5 Crore / $2M"
                          data-testid="round-size"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Amount Already Raised / Committed</Label>
                        <Input
                          value={formData.amountRaised}
                          onChange={(e) => updateField('amountRaised', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="₹2 Crore"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Amount Still Seeking</Label>
                        <Input
                          value={formData.amountSeeking}
                          onChange={(e) => updateField('amountSeeking', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="₹3 Crore"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Pre-Money Valuation *</Label>
                        <Input
                          value={formData.preMoneyValuation}
                          onChange={(e) => updateField('preMoneyValuation', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="₹25 Crore"
                          data-testid="valuation"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Instrument *</Label>
                        <Select value={formData.instrument} onValueChange={(v) => updateField('instrument', v)}>
                          <SelectTrigger className="mt-1 bg-slate-800 border-slate-700 text-slate-100" data-testid="instrument">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="equity">Equity (CCD / CCPS)</SelectItem>
                            <SelectItem value="convertible-note">Convertible Note</SelectItem>
                            <SelectItem value="safe">SAFE</SelectItem>
                            <SelectItem value="debt">Debt / Revenue-based financing</SelectItem>
                            <SelectItem value="open">Open to discussion</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-300">Minimum Ticket Size (per investor)</Label>
                        <Input
                          value={formData.minTicketSize}
                          onChange={(e) => updateField('minTicketSize', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="₹25 Lakhs"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Target Round Close Date</Label>
                        <Input
                          type="date"
                          value={formData.targetCloseDate}
                          onChange={(e) => updateField('targetCloseDate', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                        />
                      </div>
                    </div>
                  </Card>

                  <Card className="bg-slate-800/40 border-slate-700 p-6">
                    <h3 className="text-lg font-semibold text-slate-100 mb-4">Use of Funds (%)</h3>
                    <div className="grid md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <Label className="text-slate-300">Product & Technology</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={formData.useOfFundsProduct}
                          onChange={(e) => updateField('useOfFundsProduct', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="30"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Sales & Marketing</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={formData.useOfFundsSales}
                          onChange={(e) => updateField('useOfFundsSales', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="40"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Team & Hiring</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={formData.useOfFundsTeam}
                          onChange={(e) => updateField('useOfFundsTeam', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="15"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Operations & Infrastructure</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={formData.useOfFundsOperations}
                          onChange={(e) => updateField('useOfFundsOperations', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="10"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Working Capital / Other</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={formData.useOfFundsOther}
                          onChange={(e) => updateField('useOfFundsOther', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="5"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-300">Detailed Use of Funds Narrative</Label>
                      <Textarea
                        value={formData.useOfFundsNarrative}
                        onChange={(e) => updateField('useOfFundsNarrative', e.target.value)}
                        className="mt-1 bg-slate-800 border-slate-700 text-slate-100 min-h-[100px]"
                        placeholder="Explain how you plan to deploy the capital..."
                      />
                    </div>
                  </Card>

                  <Card className="bg-slate-800/40 border-slate-700 p-6">
                    <h3 className="text-lg font-semibold text-slate-100 mb-4">Previous Funding & Strategic Fit</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Previous Total Funding Raised</Label>
                        <Input
                          value={formData.previousFunding}
                          onChange={(e) => updateField('previousFunding', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="₹1 Crore"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Previous Investors (names)</Label>
                        <Input
                          value={formData.previousInvestors}
                          onChange={(e) => updateField('previousInvestors', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="Angel Network X, Founder Y"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-slate-300">Are you open to strategic investors? What value beyond capital are you seeking?</Label>
                        <Textarea
                          value={formData.openToStrategic}
                          onChange={(e) => updateField('openToStrategic', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="Yes, looking for investors with expertise in..."
                        />
                      </div>
                    </div>
                  </Card>
                </div>
              )}

              {/* Step 4: Documents & Data Room */}
              {currentStep === 4 && (
                <div className="space-y-6">
                  <Card className="bg-slate-800/40 border-slate-700 p-6">
                    <h3 className="text-lg font-semibold text-slate-100 mb-4">Key Documents (URLs)</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Pitch Deck URL</Label>
                        <Input
                          type="url"
                          value={formData.pitchDeckUrl}
                          onChange={(e) => updateField('pitchDeckUrl', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="https://drive.google.com/..."
                          data-testid="pitch-deck-url"
                        />
                        <p className="text-xs text-slate-500 mt-1">Google Drive, Dropbox, or Docsend link</p>
                      </div>
                      <div>
                        <Label className="text-slate-300">Financial Model / Projections URL</Label>
                        <Input
                          type="url"
                          value={formData.financialModelUrl}
                          onChange={(e) => updateField('financialModelUrl', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="https://docs.google.com/..."
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Data Room Link (if available)</Label>
                        <Input
                          type="url"
                          value={formData.dataRoomUrl}
                          onChange={(e) => updateField('dataRoomUrl', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="https://..."
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Product Demo / Video Link</Label>
                        <Input
                          type="url"
                          value={formData.productDemoUrl}
                          onChange={(e) => updateField('productDemoUrl', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="https://loom.com/..."
                        />
                      </div>
                    </div>
                  </Card>

                  <Card className="bg-slate-800/40 border-slate-700 p-6">
                    <h3 className="text-lg font-semibold text-slate-100 mb-4">IP & Regulatory</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Key IP / Patents</Label>
                        <Select value={formData.ipStatus} onValueChange={(v) => updateField('ipStatus', v)}>
                          <SelectTrigger className="mt-1 bg-slate-800 border-slate-700 text-slate-100">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No IP / patents</SelectItem>
                            <SelectItem value="filed">Patents filed (provisional)</SelectItem>
                            <SelectItem value="granted">Patents granted</SelectItem>
                            <SelectItem value="trade-secrets">Trade secrets / proprietary tech</SelectItem>
                            <SelectItem value="open-source">Open source with proprietary layer</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-300">Regulatory Approvals / Licenses</Label>
                        <Input
                          value={formData.regulatoryApprovals}
                          onChange={(e) => updateField('regulatoryApprovals', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="RBI license, FDA approval, etc."
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-slate-300">Any Ongoing Legal / Regulatory Issues?</Label>
                        <Textarea
                          value={formData.legalIssues}
                          onChange={(e) => updateField('legalIssues', e.target.value)}
                          className="mt-1 bg-slate-800 border-slate-700 text-slate-100"
                          placeholder="Describe any pending legal matters..."
                        />
                      </div>
                    </div>
                  </Card>

                  <Card className="bg-slate-800/40 border-slate-700 p-6">
                    <h3 className="text-lg font-semibold text-slate-100 mb-4">Additional Information</h3>
                    <Textarea
                      value={formData.additionalInfo}
                      onChange={(e) => updateField('additionalInfo', e.target.value)}
                      className="bg-slate-800 border-slate-700 text-slate-100 min-h-[100px]"
                      placeholder="Anything else you'd like us to know about your startup?"
                    />
                  </Card>

                  <Card className="bg-slate-800/40 border-slate-700 p-6">
                    <h3 className="text-lg font-semibold text-slate-100 mb-4">Agreements</h3>
                    <div className="space-y-4">
                      <label className="flex items-start space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.termsAgreed}
                          onChange={(e) => updateField('termsAgreed', e.target.checked)}
                          className="mt-1 w-5 h-5 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500"
                          data-testid="terms-checkbox"
                        />
                        <span className="text-slate-300 text-sm">
                          I confirm all information provided is accurate and I consent to sharing my application with vetted investors on the StartupBlackbox platform. *
                        </span>
                      </label>
                      <label className="flex items-start space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.privacyAgreed}
                          onChange={(e) => updateField('privacyAgreed', e.target.checked)}
                          className="mt-1 w-5 h-5 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500"
                          data-testid="privacy-checkbox"
                        />
                        <span className="text-slate-300 text-sm">
                          I agree to the Terms of Use and Privacy Policy. *
                        </span>
                      </label>
                    </div>
                  </Card>
                </div>
              )}
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-slate-800">
              <Button
                variant="ghost"
                onClick={handlePrev}
                disabled={currentStep === 0}
                className="text-slate-300 hover:text-slate-100"
                data-testid="prev-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>
              
              {currentStep < STEPS.length - 1 ? (
                <Button
                  onClick={handleNext}
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
                  data-testid="next-btn"
                >
                  Continue
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                  data-testid="submit-btn"
                >
                  {submitting ? 'Submitting...' : 'Submit Application'}
                </Button>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default StartupOnboarding;
